import { ChevronDownIcon, MagnifyingGlassIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { zodResolver } from '@hookform/resolvers/zod';
import CT from '@marketplace-ai/tags';
import { useSelector } from '@xstate/react';
import type { ColDef, FilterChangedEvent, IRowNode, RowSelectionOptions } from 'ag-grid-community';
import {
  AllCommunityModule,
  colorSchemeLightCold,
  ModuleRegistry,
  themeQuartz,
} from 'ag-grid-community';
import { AgGridReact } from 'ag-grid-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { FormProvider } from 'react-hook-form';
import type { ActorRefFrom } from 'xstate';
import {
  AsyncCreatableSelect,
  Button,
  FormActions,
  FormDialog,
  FormLayout,
  LoadingSpinner,
  TableCard,
  TextArea,
  TextInput,
} from '~/components';
import { FilterBanner, useFilterBanner } from '~/components/agGrid/FilterBanner';
import { FormRow as Row } from '~/components/forms/FormRow';
import { FormSection as Section } from '~/components/forms/FormSection';
import { BASE_GRID_STYLE } from '~/components/Table/gridUtil';
import { useCustomForm } from '~/hooks/useCustomForm';
import { usePrivilegesRef } from '../account/vaultSelectors';
import { loadSpecialtyOptions } from './api';
import { getEmptyPrivilegesForm, getSharedColumns, sortPrivileges } from './constants';
import type { privilegesMachine } from './privileges.machine';
import { privilegesSchema } from './schemas';
import { CreatePrivilegesFormValues, PrivilegesFormValues } from './types';

const TAGS = CT.privileges.addLayout;
const FORM_TAGS = CT.privileges.form;

interface CustomPrivilegeFormProps {
  onSave: (values: PrivilegesFormValues) => void;
  onCancel: () => void;
  providerCategoryId: number;
}

const CustomPrivilegeForm = ({
  onSave,
  onCancel,
  providerCategoryId,
}: CustomPrivilegeFormProps) => {
  const methods = useCustomForm<PrivilegesFormValues>({
    resolver: zodResolver(privilegesSchema),
    defaultValues: { ...getEmptyPrivilegesForm(), userAddedFlag: true },
  });

  return (
    <FormProvider {...methods}>
      <form
        onSubmit={methods.handleSubmit(onSave)}
        id="custom-privilege-form"
        data-cy={FORM_TAGS.form}
      >
        <FormLayout className="bg-white">
          <Section componentsPerLine={2}>
            <Row>
              <AsyncCreatableSelect
                name="specialtyName"
                label="Specialty"
                cyTag={FORM_TAGS.specialtyNameField}
                required
                minChars={false}
                loadOptions={(inputValue: string) =>
                  loadSpecialtyOptions(providerCategoryId, inputValue)
                }
                noOptionsMessage="Type to create a custom specialty"
                placeholder="Search or create a specialty..."
              />
            </Row>
            <Row>
              <TextInput
                name="category"
                label="Category"
                required
                cyTag={FORM_TAGS.privilegeCategoryField}
              />
            </Row>
          </Section>
          <Section componentsPerLine={2}>
            <Row>
              <TextInput
                name="privilegeType"
                label="Privilege Type"
                required
                cyTag={FORM_TAGS.privilegeTypeField}
              />
            </Row>
            <Row>
              <TextInput
                name="assignmentDate"
                label="Assignment Date"
                type="date"
                cyTag={FORM_TAGS.assignmentDateField}
              />
            </Row>
          </Section>
          <Section componentsPerLine={1}>
            <Row>
              <TextInput
                name="privilegeName"
                label="Privilege Name"
                required
                cyTag={FORM_TAGS.privilegeNameField}
              />
            </Row>
          </Section>
          <Section componentsPerLine={1}>
            <Row>
              <TextArea name="privilegeNotes" label="Notes" cyTag={FORM_TAGS.privilegeNotesInput} />
            </Row>
          </Section>
          <FormActions>
            <Button
              type="button"
              cyTag={FORM_TAGS.cancelBtn}
              label="Cancel"
              onClick={onCancel}
              colorVariant="secondary"
            />
            <Button type="submit" cyTag={FORM_TAGS.submitBtn} label="Add to List" />
          </FormActions>
        </FormLayout>
      </form>
    </FormProvider>
  );
};

const rowSelection: RowSelectionOptions = {
  mode: 'multiRow',
  headerCheckbox: false,
};

ModuleRegistry.registerModules([AllCommunityModule]);
const theme = themeQuartz.withPart(colorSchemeLightCold).withParams(BASE_GRID_STYLE);

const AddPrivilegesGridInner = ({
  privilegesRef,
}: {
  privilegesRef: ActorRefFrom<typeof privilegesMachine>;
}) => {
  const [searchText, setSearchText] = useState('');
  const [selectedSpecialty, setSelectedSpecialty] = useState('');
  const [selectedRowData, setSelectedRowData] = useState<CreatePrivilegesFormValues[]>([]);
  const [showCustomForm, setShowCustomForm] = useState(false);
  const [pendingCustom, setPendingCustom] = useState<CreatePrivilegesFormValues[]>([]);
  const [showCloseConfirm, setShowCloseConfirm] = useState(false);
  const {
    availablePrivileges: rawAvailablePrivileges,
    loading,
    providerCategoryId,
    crudRef: privilegeCrudMachine,
  } = useSelector(privilegesRef, (state) => state.context);
  const availablePrivileges = useMemo(
    () => sortPrivileges(rawAvailablePrivileges, 'specialtyName'),
    [rawAvailablePrivileges]
  );

  const specialtyOptions = useMemo(
    () =>
      [...new Set(availablePrivileges.map((p) => p.specialtyName))].sort((a, b) =>
        a.localeCompare(b, undefined, { sensitivity: 'base' })
      ),
    [availablePrivileges]
  );

  const isExternalFilterPresent = useCallback(() => selectedSpecialty !== '', [selectedSpecialty]);

  const doesExternalFilterPass = useCallback(
    (node: IRowNode) => node.data?.specialtyName === selectedSpecialty,
    [selectedSpecialty]
  );

  useEffect(() => {
    gridRef.current?.api?.onFilterChanged();
  }, [selectedSpecialty]);

  const handleAddMultiple = () => {
    const allPrivileges = [...selectedRowData, ...pendingCustom];
    if (allPrivileges.length === 0) return;
    privilegesRef.send({
      type: 'ON_CREATE_MULTIPLE',
      payload: { privileges: allPrivileges },
    });
    setSelectedRowData([]);
    setPendingCustom([]);
    privilegeCrudMachine.send({ type: 'ON_CLOSE_MODAL' });
  };

  const handleCustomSave = (values: PrivilegesFormValues) => {
    setPendingCustom((prev) => [
      ...prev,
      {
        privilegeType: values.privilegeType,
        privilegeName: values.privilegeName,
        privilegeNotes: values.privilegeNotes,
        category: values.category,
        specialtyName: values.specialtyName,
        assignmentDate: values.assignmentDate,
        userAddedFlag: true,
      },
    ]);
    setShowCustomForm(false);
  };

  const removePendingCustom = (index: number) => {
    setPendingCustom((prev) => prev.filter((_, i) => i !== index));
  };

  const onSelectionChanged = () => {
    if (!gridRef.current) return;
    const selectedRows = gridRef.current.api.getSelectedRows();
    setSelectedRowData(selectedRows);
  };

  const [colDefs] = useState<ColDef[]>([
    { field: 'id', hide: true },
    ...getSharedColumns('specialtyName'),
  ]);

  const defaultColDef = useMemo<ColDef>(() => {
    return {
      cellStyle: { display: 'flex', alignItems: 'center' },
      filter: true,
      resizable: true,
      flex: 1,
    };
  }, []);

  const gridRef = useRef<AgGridReact>(null);
  const { filtersActive, handleFilterChanged, clearFilters, registerGridApi } = useFilterBanner();
  const clearAllFilters = () => {
    setSearchText('');
    setSelectedSpecialty('');
    clearFilters();
  };

  // MCV-1468: the specialty dropdown (an AG Grid external filter) and the
  // Specialty column filter both filter on specialtyName, and AG Grid ANDs
  // them — so two different picks cancel out to an empty grid. Keep the two
  // controls mutually exclusive: choosing one clears the other.
  const handleSpecialtyChange = (value: string) => {
    setSelectedSpecialty(value);
    const api = gridRef.current?.api;
    if (value && api) {
      const model = api.getFilterModel();
      if (model.specialtyName) {
        delete model.specialtyName;
        api.setFilterModel(model);
      }
    }
  };

  const onFilterChanged = (event: FilterChangedEvent) => {
    handleFilterChanged(event);
    const model = gridRef.current?.api?.getFilterModel() ?? {};
    if (model.specialtyName && selectedSpecialty) {
      setSelectedSpecialty('');
    }
  };

  const totalToAdd = selectedRowData.length + pendingCustom.length;

  const handleClose = useCallback(() => {
    if (totalToAdd > 0) {
      setShowCloseConfirm(true);
      return;
    }
    setSelectedRowData([]);
    setPendingCustom([]);
    privilegeCrudMachine.send({ type: 'ON_CLOSE_MODAL' });
  }, [totalToAdd, privilegeCrudMachine]);

  const confirmClose = useCallback(() => {
    setShowCloseConfirm(false);
    setSelectedRowData([]);
    setPendingCustom([]);
    privilegeCrudMachine.send({ type: 'ON_CLOSE_MODAL' });
  }, [privilegeCrudMachine]);

  return (
    <>
      <TableCard
        title="Add Privileges"
        addLabel="Add Custom Privilege"
        onAddClick={() => setShowCustomForm(true)}
        addBtnTag={TAGS.addNewBtn}
        layoutTag={TAGS.card}
      >
        <div className="mb-2 flex flex-col sm:flex-row items-stretch sm:items-center gap-2">
          <label className="flex items-center gap-2 rounded-md border border-grey-900 px-3 py-2 w-full sm:w-72">
            <MagnifyingGlassIcon className="h-4 w-4 text-grey-500" />
            <input
              aria-label="Search available privileges"
              className="w-full bg-transparent text-sm outline-none"
              placeholder="Search available privileges…"
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
            />
          </label>
          <label className="flex items-center gap-2 rounded-md border border-grey-900 px-3 py-2">
            <ChevronDownIcon className="h-4 w-4 shrink-0 text-grey-500" />
            <select
              aria-label="Filter by specialty"
              data-cy={TAGS.specialtyFilter}
              className="w-full appearance-none bg-transparent text-sm text-grey-100 outline-none"
              value={selectedSpecialty}
              onChange={(e) => handleSpecialtyChange(e.target.value)}
            >
              <option value="">All Specialties</option>
              {specialtyOptions.map((name) => (
                <option key={name} value={name}>
                  {name}
                </option>
              ))}
            </select>
          </label>
        </div>
        <FilterBanner
          active={
            filtersActive || searchText.trim().length > 0 || selectedSpecialty.trim().length > 0
          }
          onClear={clearAllFilters}
        />
        <div className="h-[520px] w-full p-0 text-grey-100" data-cy={TAGS.grid}>
          <AgGridReact
            theme={theme}
            colResizeDefault="shift"
            rowData={availablePrivileges}
            loading={loading}
            columnDefs={colDefs}
            ref={gridRef}
            defaultColDef={defaultColDef}
            quickFilterText={searchText}
            isExternalFilterPresent={isExternalFilterPresent}
            doesExternalFilterPass={doesExternalFilterPass}
            pagination={true}
            paginationAutoPageSize={true}
            onFilterChanged={onFilterChanged}
            onGridReady={(params) => registerGridApi(params.api)}
            onSelectionChanged={onSelectionChanged}
            enableFilterHandlers={true}
            rowSelection={rowSelection}
            loadingOverlayComponent={() => <LoadingSpinner />}
          />
        </div>
      </TableCard>

      {pendingCustom.length > 0 && (
        <div className="mt-2 p-3 bg-base rounded border border-grey-900">
          <h4 className="text-sm font-medium text-grey-200 mb-2">
            Custom Privileges to Add ({pendingCustom.length})
          </h4>
          <div className="space-y-1">
            {pendingCustom.map((privilege, idx) => (
              <div
                key={idx}
                className="flex items-center justify-between text-sm py-1 px-2 bg-white rounded"
              >
                <span className="text-grey-100">
                  {privilege.specialtyName.label} &mdash; {privilege.privilegeName}
                </span>
                <button
                  type="button"
                  onClick={() => removePendingCustom(idx)}
                  className="text-grey-600 hover:text-error-500 ml-2"
                  title="Remove from list"
                  aria-label={`Remove ${privilege.privilegeName} from list`}
                >
                  <XMarkIcon className="size-4" aria-hidden="true" />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="sm:grid mt-2 sm:grid-flow-row-dense sm:grid-cols-6 sm:gap-3 justify-end">
        <div className="col-span-4" />
        <Button
          type="button"
          colorVariant="secondary"
          cyTag={TAGS.cancelBtn}
          label={'Cancel'}
          onClick={handleClose}
        />
        <Button
          type="button"
          cyTag={TAGS.addMultiBtn}
          label={totalToAdd > 0 ? `Add Selected (${totalToAdd})` : 'Add Selected'}
          onClick={() => handleAddMultiple()}
          disabled={totalToAdd === 0}
        />
      </div>

      <FormDialog
        title="Add Custom Privilege"
        creating={showCustomForm}
        onClose={() => setShowCustomForm(false)}
        padding="pt-4"
        bgColor="white"
        width="4xl"
      >
        <CustomPrivilegeForm
          onSave={handleCustomSave}
          onCancel={() => setShowCustomForm(false)}
          providerCategoryId={providerCategoryId}
        />
      </FormDialog>

      {showCloseConfirm && (
        <div className="fixed inset-0 bg-grey-100/30 backdrop-blur-md flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded-lg shadow-lg max-w-md w-full mx-4">
            <h3 className="text-lg font-semibold mb-2">Unsaved Privileges</h3>
            <p className="text-grey-300 mb-6">
              You have {totalToAdd} unsaved privilege(s). Close without saving?
            </p>
            <div className="flex gap-3 justify-end">
              <Button
                type="button"
                colorVariant="secondary"
                label="Go Back"
                onClick={() => setShowCloseConfirm(false)}
              />
              <Button type="button" colorVariant="error" label="Discard" onClick={confirmClose} />
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export const AddPrivilegesGrid = () => {
  const { privilegesRef } = usePrivilegesRef();
  if (!privilegesRef) return null;
  return <AddPrivilegesGridInner privilegesRef={privilegesRef} />;
};
